import { useEffect, useState, useCallback } from 'react';

/**
 * Hook pour gérer l'API Digital Goods et Google Play Billing
 * Fonctionne uniquement dans une Trusted Web Activity (TWA)
 */
export const useDigitalGoods = () => {
  const [service, setService] = useState(null);
  const [isAvailable, setIsAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeDigitalGoods = async () => {
      try {
        // Vérifier si l'API Digital Goods est supportée
        if (!('getDigitalGoodsService' in window)) {
          console.log('Digital Goods API not available in this browser');
          setError('Digital Goods API is not available');
          setLoading(false);
          return;
        }

        // Essayer d'obtenir le service Google Play Billing
        const dgService = await window.getDigitalGoodsService(
          'https://play.google.com/billing'
        );
        
        if (dgService) {
          setService(dgService);
          setIsAvailable(true);
          console.log('Google Play Billing service initialized');
        } else {
          setError('Could not initialize Google Play Billing service');
        }
      } catch (err) {
        console.error('Digital Goods initialization error:', err);
        setError(
          'Google Play Billing is not available. Ensure the app is running in a Trusted Web Activity.'
        );
      } finally {
        setLoading(false);
      }
    };

    initializeDigitalGoods();
  }, []);

  /**
   * Récupérer les détails des produits depuis Google Play
   * Utilise le prix EXACT de Google Play (avec taxes et devise locale)
   */
  const getDetails = useCallback(
    async (itemIds) => {
      if (!service) {
        throw new Error('Digital Goods service not initialized');
      }
      
      try {
        const details = await service.getDetails(itemIds);
        // Retourner les prix EXACTEMENT comme Google Play les fournit
        return details.map(item => ({
          itemId: item.itemId,
          title: item.title,
          description: item.description,
          // Prix brut de Google Play
          price: item.price,
          // Prix formaté par Google Play (inclut devise + taxes locales)
          formattedPrice: item.price.value + ' ' + item.price.currency
        }));
      } catch (err) {
        console.error('Error getting product details:', err);
        throw err;
      }
    },
    [service]
  );

  /**
   * Effectuer un achat via Google Play Billing
   */
  const makePurchase = useCallback(
    async (sku) => {
      if (!service) {
        throw new Error('Digital Goods service not initialized');
      }

      const paymentMethods = [
        {
          supportedMethods: 'https://play.google.com/billing',
          data: { sku },
        },
      ];

      const paymentDetails = {
        total: {
          label: 'Total',
          amount: { currency: 'EUR', value: '0' },
        },
      };

      try {
        const request = new window.PaymentRequest(paymentMethods, paymentDetails);
        const paymentResponse = await request.show();
        
        // Récupérer le token d'achat
        const { purchaseToken } = paymentResponse.details;
        
        // Compléter la transaction côté client
        await paymentResponse.complete('success');
        
        return { 
          purchaseToken,
          sku
        };
      } catch (err) {
        if (err.name === 'AbortError') {
          throw new Error('Purchase was cancelled by user');
        }
        console.error('Purchase error:', err);
        throw err;
      }
    },
    [service]
  );

  /**
   * Lister les achats existants de l'utilisateur
   */
  const listPurchases = useCallback(
    async () => {
      if (!service) {
        throw new Error('Digital Goods service not initialized');
      }
      
      try {
        const purchases = await service.listPurchases();
        return purchases.map(p => ({
          itemId: p.itemId,
          purchaseToken: p.purchaseToken
        }));
      } catch (err) {
        console.error('Error listing purchases:', err);
        throw err;
      }
    },
    [service]
  );

  /**
   * Consommer un achat (pour les produits consommables comme les crédits)
   */
  const consume = useCallback(
    async (purchaseToken) => {
      if (!service) {
        throw new Error('Digital Goods service not initialized');
      }
      
      try {
        await service.consume(purchaseToken);
        console.log('Purchase consumed:', purchaseToken);
      } catch (err) {
        console.error('Error consuming purchase:', err);
        throw err;
      }
    },
    [service]
  );

  return {
    service,
    isAvailable,
    loading,
    error,
    getDetails,
    makePurchase,
    listPurchases,
    consume,
  };
};

export default useDigitalGoods;
